import { useState, useRef, useCallback, useEffect } from "react";

import { Box, Group, Radio, Stack, StackProps, Collapse, RadioProps } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import _ from "lodash";
import router from "next/router";

import { HEADCOUNT_OPTIONS, STAGE_OPTIONS } from "constants/explorer_selection_options";
import { dictionaryToSearchParams } from "utils/utils";

import { CheckboxList } from "./mantine-extended";
import { toLabelValue } from "./TitlePage/CollapsibleFilter";
import { SidebarCapRaisedSection } from "./TitlePage/SidebarCapRaisedSection";
import { useSearchRouteInfo } from "../hooks/routing";

enum CompanyStageType {
  NONE = "none",
  CAP = "cap",
  NOEMP = "noemp",
  FUNDING = "funding",
}

export function CompanyStageSelector(props: Omit<StackProps, "children">): JSX.Element {
  const { baseRoute, query, replaceQuery, queryString, focusedJobTitle, searchMode } =
    useSearchRouteInfo();

  function getInitialValue(): CompanyStageType {
    if (!_.isEmpty(query.capitalRaised)) {
      return CompanyStageType.CAP;
    } else if (!_.isEmpty(query.headcount)) {
      return CompanyStageType.NOEMP;
    } else if (!_.isEmpty(query.stage)) {
      return CompanyStageType.FUNDING;
    } else {
      return CompanyStageType.NONE;
    }
  }

  const [value, setValue] = useState<CompanyStageType>(getInitialValue());

  const nextRouteData = useRef(_.clone(query));

  useEffect(() => {
    nextRouteData.current = _.clone(query);
  }, [queryString, query]);

  const handleChange = useCallback(
    (name: "capitalRaised" | "headcount" | "stage", v: string[] | [number, number] | null) => {
      const nextQuery = nextRouteData.current;
      if (v) {
        _.set(nextQuery, name, v);
      } else if (name === "capitalRaised") {
        _.set(nextQuery, "capitalRaised", null);
      } else {
        _.set(nextQuery, name, []);
      }

      _.set(nextQuery, "offset", 0);

      // set next query to empty for the other 2 name options besides the one being changed
      if (name === "capitalRaised") {
        _.set(nextQuery, "headcount", []);
        _.set(nextQuery, "stage", []);
      } else if (name === "headcount") {
        _.set(nextQuery, "capitalRaised", null);
        _.set(nextQuery, "stage", []);
      } else if (name === "stage") {
        _.set(nextQuery, "capitalRaised", null);
        _.set(nextQuery, "headcount", []);
      }

      const nextQueryString = dictionaryToSearchParams(nextQuery);
      router.push(`${baseRoute}/${nextQueryString}`, undefined, { scroll: false });
    },
    [],
  );

  const radioStyles: RadioProps["styles"] = {
    label: {
      cursor: "pointer",
      fontSize: 14,
    },
  };

  return (
    <Radio.Group
      value={value}
      // @ts-expect-error TS2322
      onChange={setValue}
      name="CompanyStageType"
      size="xs"
      labelProps={{
        // cursor on hover
        sx: {
          cursor: "pointer",
          fontSize: 14,
        },
      }}
    >
      <Stack gap={0} w="100%" {...props}>
        {/*
         // @ts-expect-error TS2741 */}
        <Toggle
          trigger={
            <Radio
              styles={radioStyles}
              value={CompanyStageType.NONE}
              label="None"
              onClick={() => {
                setValue(CompanyStageType.NONE);
                // should replace query if any of query.capitalRaised || query.headcount || query.stage are not empty arrays
                const shouldReplaceQuery =
                  !_.isEmpty(query.capitalRaised) ||
                  !_.isEmpty(query.headcount) ||
                  !_.isEmpty(query.stage);
                if (shouldReplaceQuery) {
                  replaceQuery({
                    capitalRaised: null,
                    headcount: [],
                    stage: [],
                  });
                }
              }}
            />
          }
        />
        <Toggle
          withArrow
          trigger={
            <Radio styles={radioStyles} value={CompanyStageType.FUNDING} label="Funding Stage" />
          }
          opened={value === CompanyStageType.FUNDING}
        >
          <CheckboxList
            key={value}
            data={STAGE_OPTIONS.map(toLabelValue).map(handleEPlus)}
            onChange={(v: string[]) => {
              handleChange("stage", v);
            }}
            selected={_.get(query, "stage")}
          />
        </Toggle>
        <Toggle
          withArrow
          trigger={
            <Radio styles={radioStyles} value={CompanyStageType.CAP} label="Capital Raised" />
          }
          opened={value === CompanyStageType.CAP}
        >
          <SidebarCapRaisedSection
            noToggle
            // @ts-expect-error TS2322
            handleChange={handleChange}
            queryParamName="capitalRaised"
          />
        </Toggle>
        <Toggle
          withArrow
          trigger={
            <Radio
              value={CompanyStageType.NOEMP}
              label="Number of employees"
              styles={radioStyles}
            />
          }
          opened={value === CompanyStageType.NOEMP}
        >
          <CheckboxList
            key={value}
            data={HEADCOUNT_OPTIONS.map(toLabelValue)}
            onChange={(v) => {
              handleChange("headcount", v);
            }}
            selected={_.get(query, "headcount")}
          />
        </Toggle>
      </Stack>
    </Radio.Group>
  );
}

interface Toggle {
  opened?: boolean;
  withArrow?: boolean;
  children: React.ReactNode;
  trigger?: React.ReactNode;
  onClick?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function Toggle({ trigger, children, withArrow = false, opened = false, onClick }: Toggle) {
  return (
    <Box w="100%" py={8}>
      <Group w="100%" px={8}>
        <Group>
          {trigger}
          {withArrow && (
            <IconChevronDown
              size={16}
              style={{
                transform: opened ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease-in-out",
                marginLeft: 8,
              }}
            />
          )}
        </Group>
      </Group>
      <Collapse in={opened} transitionDuration={200}>
        <Box px={8} mt={8}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

function handleEPlus(obj: { label: string; value: string }): { label: string; value: string } {
  // if value is "Series E" or "Series E+" then change it to "Series E+"
  if (obj.value === "Series E") {
    return { label: "Series E+", value: obj.value };
  } else {
    return obj;
  }
}
